import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { Modal, Field, inputCls, Btn, ConfirmDialog, AdminImageUpload } from "./kit";

const REC_EMPTY = { image: "", title: "", description: "", date: "", destination: "", shipment_ref: "", quantity: "" };

export default function AdminExportJourney() {
  const [steps, setSteps] = useState([]);
  const [records, setRecords] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(REC_EMPTY);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([api.get("/export-journey"), api.get("/admin/export-records")]);
      setSteps(s.data); setRecords(r.data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const saveStep = async (st) => { try { await api.put(`/admin/export-journey/${st.id}`, { title: st.title, description: st.description, image: st.image }); toast.success("Step saved"); } catch (e) { toast.error("Failed"); } };
  const setStep = (id, k, v) => setSteps((arr) => arr.map((s) => s.id === id ? { ...s, [k]: v } : s));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const saveRec = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try { await api.post("/admin/export-records", form); toast.success("Record added"); setModal(false); setForm(REC_EMPTY); load(); } catch (e) { toast.error("Failed"); }
  };
  const delRec = async () => { try { await api.delete(`/admin/export-records/${confirm}`); toast.success("Deleted"); setConfirm(null); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <div>
      <div className="mb-6"><h1 className="font-display text-2xl font-extrabold text-offwhite">Export Journey</h1><p className="mt-1 font-body text-sm text-offwhite/55">Edit the 8-step process and manage export track record photos.</p></div>

      <h3 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-amber-soft">Process Steps</h3>
      <div className="mb-8 space-y-3">
        {steps.map((s) => (
          <div key={s.id} className="rounded-xl border border-offwhite/10 bg-[#101c14] p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-display text-sm font-bold text-offwhite">{s.step}</div>
              <div className="grid flex-1 grid-cols-1 gap-3 lg:grid-cols-2">
                <Field label="Title"><input className={inputCls} value={s.title} onChange={(e) => setStep(s.id, "title", e.target.value)} /></Field>
                <Field label="Description"><input className={inputCls} value={s.description} onChange={(e) => setStep(s.id, "description", e.target.value)} /></Field>
                <div className="lg:col-span-2"><AdminImageUpload label="Step Photo (optional)" value={s.image} onChange={(v) => setStep(s.id, "image", v)} /></div>
              </div>
              <Btn variant="outline" onClick={() => saveStep(s)}><Save className="mr-1 inline h-3.5 w-3.5" /> Save</Btn>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-amber-soft">Export Track Record ("Export in Action")</h3>
        <Btn onClick={() => { setForm(REC_EMPTY); setModal(true); }} data-testid="add-record-btn"><Plus className="mr-1 inline h-4 w-4" /> Add Record</Btn>
      </div>
      <p className="mb-4 rounded-lg bg-amber-brand/10 px-4 py-2 font-body text-xs text-amber-soft">Redact sensitive information (phone, addresses, bank/tax IDs, buyer details) on documents before uploading.</p>
      {records.length === 0 ? <div className="rounded-xl border border-dashed border-offwhite/15 py-12 text-center font-body text-sm text-offwhite/40">No export records yet.</div> : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {records.map((r) => (
            <div key={r.id} className="group relative overflow-hidden rounded-xl border border-offwhite/10 bg-[#101c14]">
              {r.image ? <img src={r.image} alt={r.title} className="aspect-[4/3] w-full object-cover" /> : <div className="aspect-[4/3] bg-offwhite/5" />}
              <div className="p-3"><div className="font-body text-xs font-semibold text-offwhite/85">{r.title}</div><div className="font-mono text-[9px] text-offwhite/45">{[r.destination, r.date].filter(Boolean).join(" · ")}</div></div>
              <button onClick={() => setConfirm(r.id)} className="absolute right-2 top-2 rounded-full bg-rose-600 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Add Export Record" wide>
        <div className="space-y-4">
          <AdminImageUpload value={form.image} onChange={(v) => set("image", v)} testId="record-image-upload" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Title *"><input data-testid="record-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
            <Field label="Date"><input className={inputCls} value={form.date} onChange={(e) => set("date", e.target.value)} placeholder="e.g. Mar 2026" /></Field>
            <Field label="Destination"><input className={inputCls} value={form.destination} onChange={(e) => set("destination", e.target.value)} /></Field>
            <Field label="Shipment Reference"><input className={inputCls} value={form.shipment_ref} onChange={(e) => set("shipment_ref", e.target.value)} /></Field>
            <Field label="Quantity (optional)"><input className={inputCls} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></Field>
          </div>
          <Field label="Description"><textarea rows={2} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <div className="flex justify-end gap-3"><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={saveRec} data-testid="save-record-btn">Save</Btn></div>
        </div>
      </Modal>
      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={delRec} message="Delete this export record?" />
    </div>
  );
}
